/**
 * Where a review comment goes inside the paragraph its bookmarks resolve to, and
 * the idempotent write of a comment onto that range.
 *
 * A review names the paragraph a comment belongs to by bookmark and says where in
 * it the comment sits (`CommentPlacement`): on the quoted words, on the first word,
 * or on the first word of the paragraph inserted after it. Resolving that placement
 * costs Word round trips (`context.sync()`), and on a long contract each round trip
 * is one to three seconds. Bulk apply resolves every comment of a batch together,
 * so the batch pays for its passes once rather than once per comment
 * (`commentTargets`); a single comment resolves through the same passes with a
 * batch of one (`commentTarget`).
 *
 * For an account with the proposal engine, the quoted words are found in the
 * paragraph's accepted text, by the engine's reader and its one occurrence finder,
 * and Word's ranges placed on that text give the range the words stand on
 * (`AcceptedTextWordRanges`). Word's own search reads the paragraph with its
 * tracked deletions still in it, so a quote with a deletion between two of its
 * words is never found there whole.
 *
 * Every comment comes back with where it landed: as placed, or relocated to the
 * paragraph's first word and why (`CommentTarget`), so the card can say so.
 */
import type { CommentPlacement } from "@common/types/issue";
import { EditOperations } from "@common/utils/redline/edit-operations";
import { OoxmlRange, type BookmarkedPage, type BookmarkedPages } from "@common/utils/redline/ooxml-range";
import { WordTokenizer } from "@common/utils/redline/text/word-tokenizer";

import { AcceptedTextWordRanges } from "./anchor/office/accepted-text-word-ranges";
import { WORD_SEPARATORS, queueAcceptedTextsOf, queueWordRangesOf } from "./anchor/office/word-text-ranges";
import type { BulkApplyBodyRead } from "./bulk-apply-body-read";
import { bulkApplyClockMs, recordBulkApplySpan, timedBulkApplySpan } from "./bulk-apply-run-timeline";
import { ProposalEngineAccess } from "./proposal-engine-access";
import { excerptOf } from "./text-excerpt";

const anchorsMatch = (relation: Word.LocationRelation): boolean =>
    relation === Word.LocationRelation.equal;

export const rangeAlreadyCarriesComment = async (range: Word.Range, commentText: string): Promise<boolean> => {
    const context = range.context;
    const existingComments = range.getComments();
    context.load(existingComments, "items/content");
    await context.sync();

    const sameText = existingComments.items.filter(
        (comment) => comment.content?.trim() === commentText.trim(),
    );
    if (sameText.length === 0) return false;

    const anchorRelations = sameText.map((comment) => comment.getRange().compareLocationWith(range));
    await context.sync();

    return anchorRelations.some((relation) => anchorsMatch(relation.value));
};

export const applyCommentChange = async (range: Word.Range, commentText: string): Promise<void> => {
    if (!commentText?.trim()) throw new Error("Invalid comment: must be a non-empty string");
    const context = range.context;

    if (await rangeAlreadyCarriesComment(range, commentText)) return;

    range.select();
    await context.sync();
    range.insertComment(commentText);
    await context.sync();
};

/**
 * Where a review comment goes within the range its bookmarks resolve to.
 */
export interface CommentTargetRequest {
    /** The range the comment's bookmarks resolve to: the paragraph(s) the review named. */
    bookmarkedRange: Word.Range;
    /**
     * The bookmarks `bookmarkedRange` was resolved from, so a batch on the proposal engine can cut
     * the paragraph's page from one read of the document body instead of reading the range
     * (`BatchBodyPages`).
     *
     * NULL
     *   Absent, or empty: the paragraph is read from `bookmarkedRange` itself, as it always was.
     */
    bookmarkNames?: string[];
    /** Where the review placed the comment; null when it did not say (a production review). */
    placement: CommentPlacement | null;
    /** The words a "span" comment sits on; null for every other placement. */
    anchorText: string | null;
}

/**
 * Why a comment is written on the paragraph's first word instead of where the review
 * placed it.
 *
 * CASES
 *   - "quote_not_in_paragraph"  -> no stretch of the paragraph reads as the quoted words:
 *                                  the paragraph changed since the review, or the quote is blank
 *   - "quote_not_found_in_word" -> the words could not be placed in Word: its ranges do not read
 *                                  the paragraph as the engine's reader does and its search missed
 *                                  them, or the account has no proposal engine and both of Word's
 *                                  searches missed them
 *   - "paragraph_after_gone"    -> an "after_paragraph" comment whose paragraph no longer follows
 *   - "word_refused_placement"  -> Word refused the comment where it was placed, and took it on
 *                                  the paragraph's whole content (`retryRefusedCommentInsert`)
 */
export type CommentRelocationReason = "quote_not_in_paragraph" | "quote_not_found_in_word" | "paragraph_after_gone" | "word_refused_placement";

/** A comment written on the range its placement names: its quoted words, the first word, the paragraph after, or the whole range. */
export interface CommentTargetAsPlaced {
    range: Word.Range;
    landing: "as_placed";
}

/** A comment written on the paragraph's first word because its placement could not be honoured, and why. */
export interface CommentTargetRelocated {
    range: Word.Range;
    landing: "relocated";
    relocationReason: CommentRelocationReason;
}

/** Where a comment is written, and whether that is where the review placed it. */
export type CommentTarget = CommentTargetAsPlaced | CommentTargetRelocated;

/** A comment written somewhere other than where the review placed it: why, and the words it landed on as Word reads them. */
export interface CommentRelocation {
    relocationReason: CommentRelocationReason;
    landedOnText: string;
}

/** What the card says after the words a relocated comment sits on, by why it is not on its own words. */
const RELOCATION_SENTENCE_BY_REASON: Record<CommentRelocationReason, (quotedWords: string) => string> = {
    quote_not_in_paragraph: (quotedWords) => `${quotedWords} is no longer in this paragraph.`,
    quote_not_found_in_word: (quotedWords) => `Word could not find ${quotedWords} in this paragraph.`,
    paragraph_after_gone: () => "the paragraph it was written for no longer follows this one.",
    word_refused_placement: () => "Word would not take the comment where it was placed.",
};

/**
 * How much of the words a relocated comment landed on the card quotes: a comment Word took
 * on the paragraph's whole content would otherwise quote the whole paragraph.
 *
 * EXAMPLE
 *   a 300-character paragraph -> its first 59 characters and "…"
 */
const LANDED_ON_EXCERPT_CHARACTERS = 60;

/**
 * The sentence a card shows for a comment that was applied but relocated: the words it
 * sits on, and why it is not on its own.
 *
 * CASES, for the quoted words
 *   - the review quoted words -> those words, in quotation marks
 *   - it quoted none          -> "its quote"
 *
 * EXAMPLE
 *   landed on "CONSULTANT", quote "shall not exceed the lesser of", "quote_not_in_paragraph"
 *   -> 'Placed on "CONSULTANT": "shall not exceed the lesser of" is no longer in this paragraph.'
 *   the same, `placedSubject` 'Comment “Pegging the cap…” placed'
 *   -> 'Comment “Pegging the cap…” placed on "CONSULTANT": "shall not exceed the lesser of" is no longer in this paragraph.'
 */
export const relocatedCommentSentenceOf = (relocation: CommentRelocation, quoteText: string | null, placedSubject = "Placed"): string => {
    /** NULL quote: an after_paragraph comment, which quotes nothing; read as "". */
    const trimmedQuote = quoteText?.trim() ?? "";
    /**
     * CASES
     *   - the review quoted words -> those words, in quotation marks
     *   - it quoted none          -> "its quote"
     */
    const quotedWords = trimmedQuote.length > 0 ? `"${trimmedQuote}"` : "its quote";
    return `${placedSubject} on "${excerptOf(relocation.landedOnText, LANDED_ON_EXCERPT_CHARACTERS)}": ${RELOCATION_SENTENCE_BY_REASON[relocation.relocationReason](quotedWords)}`;
};

/**
 * Word's `Range.search` takes at most this many characters of search text.
 */
const WORD_SEARCH_TEXT_MAX_CHARS = 255;

/**
 * What stands between two paragraphs' accepted texts when a bookmarked range holds
 * more than one: a line break, which the tokenizer reads as whitespace, as Word's
 * ranges cut at the paragraph's end.
 */
const PARAGRAPH_BREAK_IN_ACCEPTED_TEXT = "\n";

/**
 * The text Word is asked to find for a "span" comment: the quoted words with their
 * spacing collapsed, cut at the last word boundary inside Word's search limit when
 * the quote is longer.
 *
 * EXAMPLE
 *   "thirty  (30)\n days"   -> "thirty (30) days"
 *   a 300-character quote   -> its opening words, at most 255 characters, whole words only
 *
 * WHY the cut
 *   A quote longer than the limit makes the search throw; cut, the comment sits on the
 *   quote's opening words instead of failing.
 */
const wordSearchText = (anchorText: string): string => {
    const collapsed = anchorText.replace(/\s+/g, " ").trim();
    if (collapsed.length <= WORD_SEARCH_TEXT_MAX_CHARS) return collapsed;
    /**
     * EXAMPLE
     *   collapsed = "aaa bbb … zzz" (300 chars), a space at index 251 and none between 252 and 255
     *   -> lastBoundary = 251, the text is the 251 characters before that space
     */
    const lastBoundary = collapsed.lastIndexOf(" ", WORD_SEARCH_TEXT_MAX_CHARS);
    return collapsed.slice(0, lastBoundary > 0 ? lastBoundary : WORD_SEARCH_TEXT_MAX_CHARS);
};

/**
 * How many words from each end of a quote the edge search uses when the whole quote
 * is not in Word's text. For an account with the proposal engine a word is a unit of the
 * redline diff (`WordTokenizer.diffUnits`), so a Japanese, Chinese or Thai quote is counted
 * in its own words; without the engine it is a run of characters between spaces.
 */
const EDGE_SEARCH_WORD_COUNT = 4;

/** The quote's opening and closing words, each as one stretch of the quote for Word to search. */
interface QuoteEdgeSearchTexts {
    openingEdgeText: string;
    closingEdgeText: string;
}

/** How a batch cuts a missed quote into the edges Word is asked to find. */
type QuoteEdgeSearchTextsCut = (spanSearchText: string) => QuoteEdgeSearchTexts | null;

/**
 * The text Word is asked to find for each edge of a quote, for an account with the
 * proposal engine: its first and its last `EDGE_SEARCH_WORD_COUNT` words, each sliced
 * from the quote with whatever stood between those words, so an edge is always the
 * quote's own characters.
 *
 * WHEN the engine asks for edges
 *   The paragraph read placed the quote on a Word range that reaches past it: a
 *   Japanese, Chinese or Thai sentence Word cuts as one range. The edges narrow it.
 *
 * WHY the redline diff's units
 *   Japanese, Chinese and Thai put no spaces between words. Split at spaces, a quote in
 *   them was one word, too few for two edges, and the comment fell back to the first
 *   word Word cuts at a space: the whole paragraph.
 *
 * EXAMPLE
 *   quote "This Agreement is governed by the laws of the State of California, …"
 *   opening -> "This Agreement is governed", closing -> "conflict of laws principles."
 *
 *   quote "本契約は、両当事者が署名した日から効力を生じ、二年間存続するものとする。", 24 units
 *   opening -> units 0..3 "本" "契約" "は" "、", the slice "本契約は、"
 *   closing -> units 20..23 "もの" "と" "する" "。", the slice "ものとする。"
 *
 * NULL
 *   a quote of fewer than twice `EDGE_SEARCH_WORD_COUNT` words: its two edges would
 *   share words, so they could not frame a stretch between them.
 */
const quoteEdgeSearchTextsInUnits: QuoteEdgeSearchTextsCut = (spanSearchText) => {
    const quoteUnits = WordTokenizer.diffUnits(spanSearchText);
    /**
     * WHEN the quote holds fewer than 8 units ("Fees are due in thirty days.", 6 words)
     *   CONSEQUENCE
     *     no edges: the caller goes straight to the read's range or the first-word fallback.
     */
    if (quoteUnits.length < EDGE_SEARCH_WORD_COUNT * 2) return null;
    /**
     * EXAMPLE
     *   EDGE_SEARCH_WORD_COUNT = 4 -> `4 - 1` = 3, the last unit of the opening edge
     *   quoteUnits.length = 24     -> `24 - 4` = 20, the first unit of the closing edge;
     *                                 `24 - 1` = 23, the quote's last unit
     */
    const lastOpeningUnit = quoteUnits[EDGE_SEARCH_WORD_COUNT - 1];
    const firstClosingUnit = quoteUnits[quoteUnits.length - EDGE_SEARCH_WORD_COUNT];
    const lastQuoteUnit = quoteUnits[quoteUnits.length - 1];
    return {
        /**
         * EXAMPLE
         *   "本" starts at 0, "、" (unit 3) ends at 5 -> slice(0, 5) = "本契約は、"
         *   the end offset is the first character after the unit, so the slice keeps "、"
         */
        openingEdgeText: spanSearchText.slice(quoteUnits[0].startOffset, lastOpeningUnit.endOffset),
        /**
         * EXAMPLE
         *   "もの" (unit 20) starts at 30, "。" (unit 23) ends at 36, the quote's length
         *   -> slice(30, 36) = "ものとする。"
         */
        closingEdgeText: spanSearchText.slice(firstClosingUnit.startOffset, lastQuoteUnit.endOffset),
    };
};

/**
 * The text Word is asked to find for each edge of a quote, for an account without the
 * proposal engine: its first and last `EDGE_SEARCH_WORD_COUNT` words split at spaces,
 * as the comment placement has always cut them there.
 *
 * WHY accounts without the engine keep this cut
 *   A change to placement every account shares stays on the proposal engine's path,
 *   so an account without the engine places its comments as it always has.
 *
 * EXAMPLE
 *   quote "This Agreement is governed by the laws of the State of California, …", 18 words
 *   slice(0, 4)  -> "This Agreement is governed"
 *   slice(-4)    -> "conflict of laws principles.", the last 4 words
 *
 *   quote "本契約は、両当事者が署名した日から効力を生じ、二年間存続するものとする。" -> one word: null
 *
 * NULL
 *   a quote of fewer than twice `EDGE_SEARCH_WORD_COUNT` space-separated words.
 */
const quoteEdgeSearchTextsSplitAtSpaces: QuoteEdgeSearchTextsCut = (spanSearchText) => {
    const quoteWords = spanSearchText.split(" ");
    /**
     * WHEN the quote holds fewer than 8 space-separated words, a Japanese clause among them
     *   CONSEQUENCE
     *     no edges: the caller goes straight to the first-word fallback.
     */
    if (quoteWords.length < EDGE_SEARCH_WORD_COUNT * 2) return null;
    return {
        openingEdgeText: quoteWords.slice(0, EDGE_SEARCH_WORD_COUNT).join(" "),
        closingEdgeText: quoteWords.slice(-EDGE_SEARCH_WORD_COUNT).join(" "),
    };
};

/**
 * The search text a "span" comment carries, or "" when it is not a span comment or
 * quotes nothing, so the passes below can test one string instead of two fields.
 */
const spanSearchTextOf = (request: CommentTargetRequest): string =>
    request.placement === "span" && request.anchorText !== null ? wordSearchText(request.anchorText) : "";

/**
 * A search for `searchText` inside `range`, queued on the range's context and loaded,
 * so the batch's next `sync` answers it together with every other queued search.
 *
 * WHY each hit's text is loaded
 *   A quoted span settles on its one hit when the hit reads as the quote itself
 *   (`settlesOnItsOneWholeQuoteHit`), and the text is what says so.
 */
const queueSearchIn = (range: Word.Range, searchText: string): Word.RangeCollection => {
    const hits = range.search(searchText, { matchCase: true });
    hits.load("items/text");
    return hits;
};

/**
 * The one character a comment's words are cut at. A first-word comment on
 * "4.<tab>Fees and Payment" sits on "4.<tab>Fees", and a tab inside a word keeps
 * that word whole.
 */
const COMMENT_WORD_SEPARATORS = [" "];

/**
 * The words of `range`, queued and loaded, so the first of them can be read after
 * the batch's next `sync`.
 */
const queueWordsOf = (range: Word.Range): Word.RangeCollection => queueWordRangesOf(range, COMMENT_WORD_SEPARATORS);

/**
 * What the passes read of a quoted span's paragraph, for an account with the proposal
 * engine: the paragraph's OOXML, which the engine's reader gives the accepted text of,
 * and Word's ranges cut at the tokenizer's separators with the accepted text Word
 * reads in each, filled in by pass 2.
 */
interface QuoteParagraphRead {
    /** The quoted words as the review gave them, found in the accepted text under the anchor fold. */
    quoteText: string;
    paragraphOoxml: ParagraphOoxmlRead;
    tokenizerWordRanges: Word.RangeCollection;
    tokenizerWordAcceptedTexts: OfficeExtension.ClientResult<string>[];
}

/**
 * A paragraph's OOXML once the batch's sync has answered it: Word's own answer to
 * `getOoxml()` on the range, or the page cut from the batch's one body read.
 */
interface ParagraphOoxmlRead {
    value: string;
}

/**
 * A quoted span whose paragraph is read from the batch's one body read, not from its own range:
 * the bookmarks its page is cut by, and the page once cut.
 */
interface QuoteParagraphPageRead {
    /** The bookmarks the request's range was resolved from, which name the paragraph(s) of the page. */
    bookmarkNames: string[];
    /** The quoted words as the review gave them. */
    quoteText: string;
    /**
     * The page's package, cut after pass 1's sync (`settleUnchangedParagraphTargets`).
     *
     * NULL
     *   Before the cut, or when the body read holds no plain page for the bookmarks: the
     *   paragraph is then read from its own range.
     */
    pagePackageOoxml: string | null;
}

/**
 * One comment's requests to Word while the batch resolves, filled in pass by pass.
 *
 * Every field but `request` starts empty and is set by the pass that queues it, so
 * a later pass can tell what an earlier one asked for by which fields are set.
 */
interface PendingCommentTarget {
    request: CommentTargetRequest;
    /** The quote as Word is asked to find it; "" for every placement but a quoted span. */
    spanSearchText: string;
    /** Pass 1, span with a quote on the proposal engine and no bookmarks on the request: the paragraph read the quote is found in. Set in the paragraph-reads pass when the bookmarks are known. */
    quoteParagraphRead: QuoteParagraphRead | null;
    /** Pass 1, span with a quote on the proposal engine and bookmarks on the request: the paragraph is read from the batch's one body read; null for every other comment. */
    quoteParagraphPageRead: QuoteParagraphPageRead | null;
    /** Pass 1, span: hits for the whole quote. */
    wholeQuoteHits: Word.RangeCollection | null;
    /** Pass 1, after_paragraph: the paragraph following the bookmarked one, `isNullObject` loaded. */
    followingParagraph: Word.Paragraph | null;
    /** Pass 2, span miss or a span read on the proposal engine: hits for the quote's opening words. */
    openingEdgeHits: Word.RangeCollection | null;
    /** Pass 2, span miss or a span read on the proposal engine: hits for the quote's closing words. */
    closingEdgeHits: Word.RangeCollection | null;
    /** Pass 3, a span read on the proposal engine: the range the read placed the quote on, when it reaches past the quote. */
    widenedQuoteRange: Word.Range | null;
    /** Pass 3, span miss: where each closing hit sits relative to the opening hit. */
    closingEdgeRelations: OfficeExtension.ClientResult<Word.LocationRelation>[];
    /** The range whose first word is the fallback: the bookmarked range, or the following paragraph. */
    fallbackRange: Word.Range;
    /** Pass 1 or 2: the words of `fallbackRange`, for the first-word placement and every fallback. */
    fallbackWords: Word.RangeCollection | null;
    /** Queued with `fallbackWords`: the tracked changes of `fallbackRange`, null where Word cannot list them (`queueTrackedChangesOf`). */
    fallbackTrackedChanges: Word.TrackedChangeCollection | null;
    /** Why landing on the fallback's first word relocates this comment; null when the first word is its placement. */
    relocationReasonOnFallback: CommentRelocationReason | null;
    /** The target once known; null until the pass that decides it. */
    resolvedTarget: CommentTarget | null;
    /** The words of the range the target settled on its first word of (`settleOnFirstWord`); empty for a quoted span, an edge span, or a range with no word. */
    wordsOfFirstWordTarget: Word.Range[];
    /** How many of those words, from the first, pass 4 has found struck whole; the shown word is looked for after them. */
    wordsFoundStruckWhole: number;
    /** Pass 4: the window of words asked about this round, and where each struck stretch sits against each (`queueStruckStretchesAgainstNextWords`); null when none is asked. */
    struckStretchesAgainstWords: StruckStretchesAgainstWords | null;
    /** True once pass 4 has nothing more to ask: the shown word was found, the range holds no struck stretch, or every word is struck whole. */
    firstWordSettled: boolean;
    /** Pass 3, a quote placed from the paragraph read in a range that holds struck text: where each struck stretch sits against the quote's two ends (`queueQuoteEdgesAgainstStruckStretches`). */
    quoteEdgesAgainstStruckStretches: QuoteEdgesAgainstStruckStretches | null;
}

/**
 * A quote's two end ranges and where each struck stretch of its range sits against them,
 * asked of Word in pass 3's sync.
 */
interface QuoteEdgesAgainstStruckStretches {
    quoteEdges: QuoteEdgeRanges;
    struckChanges: Word.TrackedChange[];
    relationsToFirstRange: OfficeExtension.ClientResult<Word.LocationRelation>[];
    relationsToLastRange: OfficeExtension.ClientResult<Word.LocationRelation>[];
}

/** A target on the range the comment's placement names. */
const asPlaced = (range: Word.Range): CommentTarget => ({ range, landing: "as_placed" });

/**
 * The target a comment lands on when it falls back to `range`.
 *
 * CASES
 *   - falling back is this comment's placement (first_word, or after_paragraph with its paragraph there) -> as placed
 *   - it is not                                                                                         -> relocated, with the reason
 */
const fallbackTargetOf = (target: PendingCommentTarget, range: Word.Range): CommentTarget =>
    target.relocationReasonOnFallback === null ? asPlaced(range) : { range, landing: "relocated", relocationReason: target.relocationReasonOnFallback };

/**
 * Why the first-word fallback relocates a comment, known before any pass: only a span's
 * fallback is decided here; an after_paragraph comment's is decided in pass 2, once Word
 * says whether its paragraph follows.
 *
 * CASES
 *   - span quoting nothing -> "quote_not_in_paragraph": no stretch of any paragraph reads as a blank quote
 *   - span with a quote    -> "quote_not_found_in_word"
 *   - anything else        -> null
 */
const relocationReasonOnFallbackOf = (placement: CommentPlacement | null, spanSearchText: string): CommentRelocationReason | null => {
    if (placement !== "span") return null;
    return spanSearchText.length === 0 ? "quote_not_in_paragraph" : "quote_not_found_in_word";
};

/**
 * A range's struck stretches and where each sits against a window of its words, asked of
 * Word in one sync: `relationOf[wordOffset][struckIndex]` is the stretch's place against the
 * word at `firstWordIndex + wordOffset` (`struckChange.getRange().compareLocationWith(word)`).
 */
interface StruckStretchesAgainstWords {
    struckChanges: Word.TrackedChange[];
    firstWordIndex: number;
    relationOf: OfficeExtension.ClientResult<Word.LocationRelation>[][];
}

/**
 * How many words the first round of pass 4 asks about. A round whose every word is struck
 * whole asks about as many words again as it has found struck, so a struck opening clause of
 * any length is passed over in a few round trips, and the usual first word in one.
 *
 * EXAMPLE
 *   a 14-word struck clause: round 1 words 1–12 all struck, round 2 words 13–24 -> the shown
 *   word is word 15, found in the second round trip
 */
const WORDS_IN_THE_FIRST_STRUCK_TEXT_WINDOW = 12;

/**
 * A comment's pending state before any pass has run.
 *
 * CASES
 *   - placement null -> resolved at once to the bookmarked range: a production
 *                       review's comment covers the whole range as it always has
 *   - anything else  -> unresolved, waiting on pass 1
 */
const pendingCommentTargetFor = (request: CommentTargetRequest): PendingCommentTarget => {
    const spanSearchText = spanSearchTextOf(request);
    return {
        request,
        spanSearchText,
        quoteParagraphRead: null,
        quoteParagraphPageRead: null,
        wholeQuoteHits: null,
        followingParagraph: null,
        openingEdgeHits: null,
        closingEdgeHits: null,
        widenedQuoteRange: null,
        closingEdgeRelations: [],
        fallbackRange: request.bookmarkedRange,
        fallbackWords: null,
        fallbackTrackedChanges: null,
        relocationReasonOnFallback: relocationReasonOnFallbackOf(request.placement, spanSearchText),
        resolvedTarget: request.placement === null ? asPlaced(request.bookmarkedRange) : null,
        wordsOfFirstWordTarget: [],
        wordsFoundStruckWhole: 0,
        struckStretchesAgainstWords: null,
        firstWordSettled: false,
        quoteEdgesAgainstStruckStretches: null,
    };
};

/**
 * The deleted changes of the target's fallback range, as Word listed them with its words.
 *
 * EMPTY
 *   Word cannot list tracked changes (WordApi 1.6), or the range holds no deletion.
 */
const struckChangesOf = (target: PendingCommentTarget): Word.TrackedChange[] =>
    target.fallbackTrackedChanges?.items.filter((trackedChange) => trackedChange.type === Word.TrackedChangeType.deleted) ?? [];

/**
 * The tracked changes of `range`, queued and loaded with their type, so a first word can
 * be told from the struck words Word glues to it.
 *
 * NULL
 *   Word cannot list tracked changes (WordApi 1.6): the first word is kept as Word cut it.
 */
const queueTrackedChangesOf = (range: Word.Range): Word.TrackedChangeCollection | null => {
    /** NULL: a host without WordApi 1.6 has no `getTrackedChanges`. */
    if (!Office.context.requirements.isSetSupported("WordApi", "1.6")) return null;
    const trackedChanges = range.getTrackedChanges();
    trackedChanges.load("items/type");
    return trackedChanges;
};

/**
 * Queues the words of the target's fallback range and its tracked changes together, for
 * the first word and the struck stretch Word may glue to it.
 */
const queueFallbackWordsOf = (target: PendingCommentTarget): void => {
    target.fallbackWords = queueWordsOf(target.fallbackRange);
    target.fallbackTrackedChanges = queueTrackedChangesOf(target.fallbackRange);
};

/**
 * Settles the target on the first word of its fallback range, as placed or relocated by
 * why the comment falls back there (`fallbackTargetOf`).
 *
 * FALLBACK
 *   A range with no word (an empty paragraph): the range itself, and no first word to trim.
 */
const settleOnFirstWord = (target: PendingCommentTarget, fallbackWords: Word.RangeCollection): void => {
    /** FALLBACK: Word answered no word; the range itself stands in, and there is no first word to trim. */
    if (fallbackWords.items.length === 0) {
        target.resolvedTarget = fallbackTargetOf(target, target.fallbackRange);
        return;
    }
    target.resolvedTarget = fallbackTargetOf(target, fallbackWords.items[0]);
    /** The words the shown first word is looked for among, should struck text hide the first. */
    target.wordsOfFirstWordTarget = fallbackWords.items;
};

/**
 * One string for a set of bookmark names, in the order a request lists them, to look a page up by.
 * A newline never occurs in a bookmark name, so two sets never share a key.
 *
 * EXAMPLE
 *   ["_dioptra_p_3", "_dioptra_p_4"] -> "_dioptra_p_3\n_dioptra_p_4"
 */
const bookmarkSetKeyOf = (bookmarkNames: string[]): string => bookmarkNames.join("\n");

/**
 * The batch's one read of the document body, from which each quoted span's paragraph page is
 * cut, so the batch never asks Word for a paragraph's OOXML once per comment.
 *
 * WHY
 *   Word answers `getOoxml()` on a range with the whole document's fonts and media in it, so a
 *   paragraph read per comment cost one such export each, and on a batch of twenty comments those
 *   reads were most of the apply. The body is read once and cut into pages
 *   (`OoxmlRange.bookmarkedPagesOf`), as the redline batch does (`readCurrentOoxmlsOfBatchEntries`).
 *
 * WHY the merges' read serves first
 *   A run that wrote its redlines through `applyBulkMergeData` already holds one read of the body,
 *   taken before those writes, and the package each merge wrote (`BulkApplyBodyRead`). A comment whose
 *   paragraphs that read covers is cut from it (the paragraphs no merge wrote as the read had them,
 *   the paragraphs a merge wrote as the merge wrote them), so the run reads the body once. Only a
 *   comment the merges' read cannot answer queues this batch's own read of the body.
 */
class BatchBodyPages {
    private bodyOoxmlRead: OfficeExtension.ClientResult<string> | null = null;
    private bookmarkedPages: BookmarkedPages | null = null;
    private readonly pagesOfTheMergesByBookmarkSet = new Map<string, BookmarkedPage>();

    constructor(private readonly bodyReadOfTheMerges: BulkApplyBodyRead | null) {}

    /**
     * Makes the page for `bookmarkNames` available once the batch's sync has run: taken from the
     * merges' read when it covers them, else the batch's own body read is queued on the context.
     *
     * CASES
     *   - the merges' read covers the bookmarks -> their page is kept; nothing is queued
     *   - it does not, no body read is queued yet -> the body read is queued
     *   - it does not, one is already queued       -> nothing more: the batch reads the body once
     *
     * EXAMPLE
     *   20 comments on paragraphs 0..19, merges wrote 0..4 -> 5 pages from the packages the merges wrote,
     *   15 from the read taken before them, 0 body reads queued
     */
    requestPageOf(wordRequestContext: Word.RequestContext, bookmarkNames: string[]): void {
        const answerOfTheMerges = this.bodyReadOfTheMerges?.pageAfterMerges(bookmarkNames);
        /** WHEN the merges' read covers the bookmarks CONSEQUENCE their page is kept and Word is not asked. */
        if (answerOfTheMerges?.covered) {
            this.pagesOfTheMergesByBookmarkSet.set(bookmarkSetKeyOf(bookmarkNames), answerOfTheMerges.page);
            return;
        }
        /** WHEN the batch's body read is already queued CONSEQUENCE it is the one; a second would read the whole document again. */
        if (this.bodyOoxmlRead !== null) return;
        this.bodyOoxmlRead = wordRequestContext.document.body.getOoxml();
    }

    /**
     * The package of the page for `bookmarkNames`: the one the merges' read gave, else cut from
     * the batch's own body read once a sync has answered it.
     *
     * NULL
     *   No page was requested, or the read holds no plain page for these bookmarks: none
     *   of them is in the read, or one is a table cell or a content control's paragraph, which
     *   Word shapes itself (`BookmarkedPageReadFromWord`). The caller reads the range instead.
     */
    pagePackageOoxmlOf(bookmarkNames: string[]): string | null {
        const pageOfTheMerges = this.pagesOfTheMergesByBookmarkSet.get(bookmarkSetKeyOf(bookmarkNames));
        /**
         * CASES
         *   - the merges' read answered these bookmarks -> its page's package, null for a paragraph Word shapes itself
         *   - it did not                                -> the batch's own body read, below
         */
        if (pageOfTheMerges !== undefined) return pageOfTheMerges.kind === "page" ? pageOfTheMerges.packageOoxml : null;
        /** WHEN no body read was queued CONSEQUENCE there is no page to cut. */
        if (this.bodyOoxmlRead === null) return null;
        this.bookmarkedPages ??= OoxmlRange.bookmarkedPagesOf(this.bodyOoxmlRead.value);
        const bookmarkedPage = this.bookmarkedPages.pageOf(bookmarkNames);
        /**
         * CASES
         *   - the read cut a page for the bookmarks -> its package
         *   - it holds none, or Word shapes it      -> null
         */
        return bookmarkedPage !== null && bookmarkedPage.kind === "page" ? bookmarkedPage.packageOoxml : null;
    }
}

/**
 * Pass 1: one request per comment, by placement.
 *
 * CASES
 *   - span with a quote   -> the whole-quote search; on the proposal engine also the paragraph,
 *                            which the quote is found and placed in: with bookmarks on the
 *                            request, its page of the batch's one body read; without, its OOXML
 *                            and Word's ranges cut at the tokenizer's separators, read from the range
 *   - span with no quote  -> the bookmarked range's words, for the first-word fallback
 *   - first_word          -> the bookmarked range's words
 *   - after_paragraph     -> the following paragraph, so pass 2 knows whose words to ask for
 *
 * WHY the whole-quote search on the proposal engine too
 *   Word's ranges do not always read a paragraph as the engine's reader does (a field
 *   whose result Word reads differently); the search still places a quote that holds no
 *   tracked deletion, at no extra round trip.
 *
 * RETURNS
 *   true when anything was queued, so the caller syncs only when there is something
 *   to answer.
 */
const queueFirstPass = (pending: PendingCommentTarget[], readsQuotesFromTheDocument: boolean, batchBodyPages: BatchBodyPages): boolean => {
    let queuedAnything = false;
    for (const target of pending) {
        if (target.resolvedTarget !== null) continue;
        const { placement, bookmarkedRange, anchorText, bookmarkNames } = target.request;
        if (placement === "span" && anchorText !== null && target.spanSearchText.length > 0) {
            target.wholeQuoteHits = queueSearchIn(bookmarkedRange, target.spanSearchText);
            /**
             * CASES, for a quoted span on an account with the proposal engine
             *   - the request names the bookmarks its range came from -> the paragraph is read from the batch's one body
             *                                                            read, queued here with the search; its words are asked
             *                                                            for only when the hit does not settle the quote
             *                                                            (`queueParagraphReadsOfUnsettledTargets`)
             *   - it names none                                        -> the paragraph's OOXML and words are read from the range, as before
             *   - the account has no proposal engine                   -> the search alone
             */
            if (readsQuotesFromTheDocument) {
                if (bookmarkNames !== undefined && bookmarkNames.length > 0) {
                    target.quoteParagraphPageRead = { bookmarkNames, quoteText: anchorText, pagePackageOoxml: null };
                    batchBodyPages.requestPageOf(bookmarkedRange.context, bookmarkNames);
                } else {
                    target.quoteParagraphRead = {
                        quoteText: anchorText,
                        paragraphOoxml: bookmarkedRange.getOoxml(),
                        tokenizerWordRanges: queueWordRangesOf(bookmarkedRange, WORD_SEPARATORS),
                        tokenizerWordAcceptedTexts: [],
                    };
                }
            }
        } else if (placement === "after_paragraph") {
            target.followingParagraph = bookmarkedRange.paragraphs.getFirst().getNextOrNullObject();
            target.followingParagraph.load("isNullObject");
        } else {
            queueFallbackWordsOf(target);
        }
        queuedAnything = true;
    }
    return queuedAnything;
};

/**
 * Whether Word's whole-quote search settles a quoted span by itself, with no read of the
 * paragraph's words: the search found the quote once, the hit reads as the quote the review
 * gave, and the paragraph holds no tracked change.
 *
 * WHY each condition
 *   Word searches the paragraph with its tracked deletions in it, and a struck stretch can
 *   sit against the hit's edges (`trimQuoteEdgesOffStruckText`); a paragraph with no tracked
 *   change has none, so the one hit stands on exactly the quote's words. More than one hit
 *   leaves the first occurrence to the paragraph read, and a hit that reads other words than
 *   the quote is no placement of it.
 *
 * CASES
 *   - the body read cut no page for the bookmarks         -> false: whether the paragraph changed is unknown
 *   - the search found no hit, or more than one           -> false: the paragraph read finds the occurrence
 *   - its one hit reads other words than the quote        -> false
 *   - the page holds an insertion, a deletion or a move   -> false: the paragraph read places the quote
 *   - otherwise                                           -> true
 *
 * EXAMPLE
 *   quote "due within thirty (30) days", one hit reading "due within thirty (30) days", a page with no
 *   `w:ins`, `w:del`, `w:moveFrom` or `w:moveTo` -> true
 *   the same page with "thirty" inserted by Lukas -> false
 */
const settlesOnItsOneWholeQuoteHit = (target: PendingCommentTarget, quoteParagraphPageRead: QuoteParagraphPageRead): boolean => {
    const { wholeQuoteHits } = target;
    /** WHEN the batch's body read cut no page, or the search was never queued CONSEQUENCE the paragraph's state is unknown. */
    if (quoteParagraphPageRead.pagePackageOoxml === null || wholeQuoteHits === null) return false;
    /** WHEN the search found no hit or several CONSEQUENCE there is no one occurrence to settle on. */
    if (wholeQuoteHits.items.length !== 1) return false;
    /** WHEN the one hit reads other words than the review's quote CONSEQUENCE it is not the quote's placement. */
    if (wholeQuoteHits.items[0].text !== quoteParagraphPageRead.quoteText) return false;
    /** CASES: a revision mark in the page (`w:ins`, `w:del`, `w:moveFrom`, `w:moveTo`) -> not settled here; none -> the hit. */
    return !new OoxmlRange(quoteParagraphPageRead.pagePackageOoxml).hasRevisionMarks();
};

/**
 * Settles every quoted span read from the body on its one whole-quote hit when the paragraph
 * is unchanged, and keeps the page of every other for the paragraph-reads pass. Runs after
 * pass 1's sync, which answered the searches and the body read; asks Word nothing.
 *
 * CASES, per quoted span read from the body
 *   - `settlesOnItsOneWholeQuoteHit` -> resolved to the hit, as placed: no word, no accepted text, no OOXML is read
 *   - otherwise                      -> unresolved, with its page (or none) kept for `queueParagraphReadsOfUnsettledTargets`
 *
 * WHY the step is a span
 *   With a timeline active, `comment_target_fast_path` says how many comments the hit settled
 *   out of how many were read from the body, and holds the time of the cut and the checks.
 *
 * EXAMPLE
 *   20 comments on unchanged paragraphs -> { resolved: 20, total: 20 }, 0 reads of a paragraph
 *   20 comments, 3 on paragraphs with a tracked change -> { resolved: 17, total: 20 }
 */
const settleUnchangedParagraphTargets = (pending: PendingCommentTarget[], batchBodyPages: BatchBodyPages): void => {
    const fastPathStartedAtMs = bulkApplyClockMs();
    let pageReadTargetCount = 0;
    let settledTargetCount = 0;
    for (const target of pending) {
        const { quoteParagraphPageRead, wholeQuoteHits } = target;
        /** WHEN the quote's paragraph is not read from the body CONSEQUENCE it is not this step's: its own passes settle it. */
        if (quoteParagraphPageRead === null || wholeQuoteHits === null) continue;
        pageReadTargetCount += 1;
        quoteParagraphPageRead.pagePackageOoxml = batchBodyPages.pagePackageOoxmlOf(quoteParagraphPageRead.bookmarkNames);
        /** WHEN the hit does not settle the quote CONSEQUENCE the target stays open for the paragraph reads. */
        if (!settlesOnItsOneWholeQuoteHit(target, quoteParagraphPageRead)) continue;
        target.resolvedTarget = asPlaced(wholeQuoteHits.items[0]);
        settledTargetCount += 1;
    }
    /** WHEN no quoted span is read from the body CONSEQUENCE nothing ran here, so no span. */
    if (pageReadTargetCount === 0) return;
    recordBulkApplySpan("comment_target_fast_path", fastPathStartedAtMs, { resolved: settledTargetCount, total: pageReadTargetCount });
};

/**
 * The paragraph reads of every quoted span the whole-quote hit did not settle: Word's ranges
 * cut at the tokenizer's separators, and the paragraph's OOXML, which is the page of the batch's
 * body read, or the range's own OOXML when the body read cut no page for its bookmarks.
 *
 * CASES, per quoted span read from the body
 *   - already resolved (`settleUnchangedParagraphTargets`) -> nothing asked
 *   - the body read cut a page for its bookmarks             -> its words; the page is its OOXML, with no Word read
 *   - it cut none                                            -> its words and the range's `getOoxml()`
 *
 * RETURNS
 *   true when anything was queued, so the caller syncs only when there is something to answer.
 *   The accepted text of each word is queued by pass 2, as for a paragraph read from its range,
 *   because a word range can be asked its accepted text only once Word has answered the words.
 */
const queueParagraphReadsOfUnsettledTargets = (pending: PendingCommentTarget[]): boolean => {
    let queuedAnything = false;
    for (const target of pending) {
        const { quoteParagraphPageRead } = target;
        /** WHEN the target is settled, or its paragraph is not read from the body CONSEQUENCE nothing is asked for it here. */
        if (target.resolvedTarget !== null || quoteParagraphPageRead === null) continue;
        const { bookmarkedRange } = target.request;
        target.quoteParagraphRead = {
            quoteText: quoteParagraphPageRead.quoteText,
            /** CASES: the body read cut a page for the bookmarks -> the page; it cut none -> the range's own OOXML, read from Word. */
            paragraphOoxml: quoteParagraphPageRead.pagePackageOoxml !== null ? { value: quoteParagraphPageRead.pagePackageOoxml } : bookmarkedRange.getOoxml(),
            tokenizerWordRanges: queueWordRangesOf(bookmarkedRange, WORD_SEPARATORS),
            tokenizerWordAcceptedTexts: [],
        };
        queuedAnything = true;
    }
    return queuedAnything;
};

/**
 * Pass 2: settle what pass 1 answered, and queue the second request for the rest.
 *
 * CASES
 *   - words already loaded (first_word, span with no quote) -> resolved to the first word
 *   - span read for the proposal engine                       -> the accepted text Word reads in
 *                                                                each of the paragraph's ranges, the
 *                                                                quote's edges in the diff's units, and
 *                                                                the bookmarked range's words for the
 *                                                                fallback
 *   - span whose whole quote hit                              -> resolved to the first hit
 *   - span whose whole quote missed                           -> the opening-edge and closing-edge
 *                                                                searches, plus the bookmarked
 *                                                                range's words for the fallback
 *   - after_paragraph, a paragraph follows                    -> that paragraph's words
 *   - after_paragraph, nothing follows                        -> the bookmarked range's words
 *                                                                FALLBACK the review's insertion is
 *                                                                gone, so the comment marks the
 *                                                                paragraph it was inserted after,
 *                                                                relocated
 *
 * WHY the edges, without the proposal engine
 *   The quote was copied from the accepted text, while Word's text still holds the
 *   deleted words of a pending change inside it, so the whole quote never matches but
 *   its two edges do when no deletion falls among them.
 *
 * EXAMPLE
 *   quote       "This Agreement is governed by the laws of the State of California, without regard to conflict of laws principles."
 *   Word's text "This Agreement is governed by the laws of the State of Delaware California, without regard to conflict of laws principles."
 *   words       18; slice(0, 4) = "This Agreement is governed"; slice(-4) = "conflict of laws principles."
 *   -> both edges hit; pass 3 runs the range from the first to the second
 *
 * EDGE CASE
 *   A quote of fewer than twice EDGE_SEARCH_WORD_COUNT words has no two distinct
 *   edges; it goes straight to the first-word fallback.
 */
/**
 * Queues the searches for a quote's opening and closing words, cut by the account's
 * cut, plus the bookmarked range's words for the fallback.
 *
 * CASES
 *   - the quote has two edges -> search both; pass 3 frames the span between their hits
 *   - it is too short         -> no edge search; the read's range or the first-word fallback decides
 */
const queueQuoteEdgeSearches = (target: PendingCommentTarget, quoteEdgeSearchTextsOf: QuoteEdgeSearchTextsCut): void => {
    const { bookmarkedRange } = target.request;
    const quoteEdges = quoteEdgeSearchTextsOf(target.spanSearchText);
    /** WHEN the quote is long enough for two edges CONSEQUENCE both are searched. */
    if (quoteEdges !== null) {
        target.openingEdgeHits = queueSearchIn(bookmarkedRange, quoteEdges.openingEdgeText);
        target.closingEdgeHits = queueSearchIn(bookmarkedRange, quoteEdges.closingEdgeText);
    }
    queueFallbackWordsOf(target);
};

const queueSecondPass = (pending: PendingCommentTarget[], quoteEdgeSearchTextsOf: QuoteEdgeSearchTextsCut): boolean => {
    let queuedAnything = false;
    for (const target of pending) {
        if (target.resolvedTarget !== null) continue;
        if (target.fallbackWords !== null) {
            settleOnFirstWord(target, target.fallbackWords);
            continue;
        }
        if (target.quoteParagraphRead !== null) {
            target.quoteParagraphRead.tokenizerWordAcceptedTexts = queueAcceptedTextsOf(target.quoteParagraphRead.tokenizerWordRanges);
            queueQuoteEdgeSearches(target, quoteEdgeSearchTextsOf);
            queuedAnything = true;
            continue;
        }
        if (target.wholeQuoteHits !== null) {
            if (target.wholeQuoteHits.items.length > 0) {
                target.resolvedTarget = asPlaced(target.wholeQuoteHits.items[0]);
                continue;
            }
            queueQuoteEdgeSearches(target, quoteEdgeSearchTextsOf);
            queuedAnything = true;
            continue;
        }
        if (target.followingParagraph !== null) {
            /**
             * CASES
             *   - a paragraph follows -> its words: the comment lands where it was placed
             *   - nothing follows     -> the bookmarked range's words: the comment is relocated
             */
            if (!target.followingParagraph.isNullObject) target.fallbackRange = target.followingParagraph.getRange("Whole");
            else target.relocationReasonOnFallback = "paragraph_after_gone";
            queueFallbackWordsOf(target);
            queuedAnything = true;
        }
    }
    return queuedAnything;
};

/** The first and last of Word's ranges a quote placed from the paragraph read stands on. */
interface QuoteEdgeRanges {
    firstRange: Word.Range;
    lastRange: Word.Range;
}

/** A quoted span the paragraph read settled on its words. */
interface ParagraphReadSettled {
    kind: "settled";
    target: CommentTarget;
    /**
     * The ranges at the quote's two ends, where struck text Word glued to them may sit
     * (`trimQuoteEdgesOffStruckText`).
     *
     * NULL
     *   The quote was settled on Word's search hit, which holds the quote's own characters alone.
     */
    quoteEdges: QuoteEdgeRanges | null;
}

/** A quoted span whose words the paragraph no longer reads: it falls back to the first word, relocated. */
interface ParagraphReadQuoteGone {
    kind: "quoteGone";
}

/**
 * A quoted span the paragraph read could not settle alone: the edges or the fallback decide,
 * with the range the read placed it on when that range reaches past the quote.
 */
interface ParagraphReadUnsettled {
    kind: "unsettled";
    widenedQuoteRange: Word.Range | null;
}

type ParagraphReadPlacement = ParagraphReadSettled | ParagraphReadQuoteGone | ParagraphReadUnsettled;

/**
 * Where a quoted span lands from its paragraph read: the quote's first occurrence in
 * the paragraph's accepted text, by the one occurrence finder an edit's anchor is found
 * with (`EditOperations.occurrencesOf`), and the Word range placed on it.
 *
 * CASES
 *   - the accepted text does not hold the quote             -> gone: the caller settles it on the first
 *                                                              shown word, relocated ("quote_not_in_paragraph")
 *   - Word's ranges place it on exactly its words            -> settled on that range, as placed
 *   - Word's search found it whole                           -> settled on the search's hit, as placed
 *   - Word's ranges place it on a range reaching past it     -> unsettled, with that range: the edges may
 *                                                              narrow it, and it is kept when they do not
 *   - Word's ranges do not read as the reader                -> unsettled, with no range
 *
 * EXAMPLE
 *   quote         "shall not exceed the lesser of: … the affected goods."
 *   Word's text   "shall be limited to Two Hundred Fifty Thousand Dollars ($250,000) … [] not exceed the lesser of: …"
 *   accepted text "shall not exceed the lesser of: …"
 *   -> settled on the range from "shall" to "goods.", Laura's struck words between "shall" and "not" inside it
 */
const placementFromParagraphRead = (quoteParagraphRead: QuoteParagraphRead, wholeQuoteHits: Word.RangeCollection): ParagraphReadPlacement => {
    const acceptedText = new OoxmlRange(quoteParagraphRead.paragraphOoxml.value).allChangesAcceptedTexts().join(PARAGRAPH_BREAK_IN_ACCEPTED_TEXT);
    const [quoteOccurrence] = EditOperations.occurrencesOf(acceptedText, quoteParagraphRead.quoteText);
    /**
     * WHEN no stretch of the paragraph reads as the quote
     *   CONSEQUENCE
     *     gone: the words are no longer there, and the comment falls back to the first word
     */
    if (quoteOccurrence === undefined) return { kind: "quoteGone" };
    const tokenizerWordReadings = quoteParagraphRead.tokenizerWordRanges.items.map((wordRange, wordRangeIndex) => ({
        range: wordRange,
        acceptedText: quoteParagraphRead.tokenizerWordAcceptedTexts[wordRangeIndex].value,
    }));
    const placedWordRanges = AcceptedTextWordRanges.placedOn(acceptedText, tokenizerWordReadings);
    /**
     * CASES
     *   - Word's ranges read as the reader -> the range standing on the quote's stretch
     *   - they do not                       -> none from the read
     */
    const quoteRangeFromRead = placedWordRanges === null ? null : AcceptedTextWordRanges.spanOver(acceptedText, placedWordRanges, quoteOccurrence.start, quoteOccurrence.end);
    /**
     * WHEN the read placed the quote on exactly its words
     *   CONSEQUENCE
     *     settled there, as placed
     */
    if (quoteRangeFromRead !== null && !quoteRangeFromRead.reachesPastTheStretch) {
        return { kind: "settled", target: asPlaced(quoteRangeFromRead.range), quoteEdges: { firstRange: quoteRangeFromRead.firstRange, lastRange: quoteRangeFromRead.lastRange } };
    }
    /**
     * WHEN Word's search found the whole quote
     *   CONSEQUENCE
     *     its first hit, which stands on exactly the quote, as placed
     */
    if (wholeQuoteHits.items.length > 0) return { kind: "settled", target: asPlaced(wholeQuoteHits.items[0]), quoteEdges: null };
    /**
     * CASES
     *   - the read placed the quote on a range reaching past it -> kept for when the edges do not narrow it
     *   - it placed it nowhere                                   -> no range from the read
     */
    return { kind: "unsettled", widenedQuoteRange: quoteRangeFromRead === null ? null : quoteRangeFromRead.range };
};

/**
 * Queues where each struck stretch of the quote's range sits against the quote's first and
 * last ranges, for `trimQuoteEdgesOffStruckText`.
 *
 * WHY
 *   Word cuts words at spaces over the text it shows, struck words included, and the engine
 *   writes a replaced word as its insertion and its deletion side by side. A quote ending on
 *   "$12,000" in "The monthly fee is +[$12,000]-[$15,000] per month" stands on Word's range
 *   "$12,000$15,000", and its comment covered the struck "$15,000" after the quote too: the
 *   first-word case of #2279 QA F2, at a quote's edge. Struck words between the quote's own
 *   words stay inside it.
 *
 * RETURNS
 *   true when anything was queued: false when Word could not list the range's tracked
 *   changes, or it holds no deletion.
 */
const queueQuoteEdgesAgainstStruckStretches = (target: PendingCommentTarget, quoteEdges: QuoteEdgeRanges): boolean => {
    const struckChanges = struckChangesOf(target);
    /** WHEN the range holds no struck stretch, or Word cannot list them CONSEQUENCE the quote stands on Word's ranges as they are. */
    if (struckChanges.length === 0) return false;
    target.quoteEdgesAgainstStruckStretches = {
        quoteEdges,
        struckChanges,
        relationsToFirstRange: struckChanges.map((struckChange) => struckChange.getRange(Word.RangeLocation.whole).compareLocationWith(quoteEdges.firstRange)),
        relationsToLastRange: struckChanges.map((struckChange) => struckChange.getRange(Word.RangeLocation.whole).compareLocationWith(quoteEdges.lastRange)),
    };
    return true;
};

/**
 * Pass 3: settle every quoted span read for the proposal engine, then, for every span
 * whose edges were searched and both found, ask where each closing hit sits relative
 * to the opening hit.
 *
 * WHY the paragraph reads settle here
 *   Pass 2's sync answered the accepted text of every range, so the read needs no
 *   further round trip.
 *
 * WHY a third pass for the edges
 *   `compareLocationWith` needs both ranges to exist in Word, so it cannot be queued
 *   before the edge searches have been answered.
 */
const queueThirdPass = (pending: PendingCommentTarget[]): boolean => {
    let queuedAnything = false;
    for (const target of pending) {
        if (target.resolvedTarget !== null) continue;
        const { quoteParagraphRead, wholeQuoteHits, fallbackWords } = target;
        /**
         * WHEN the quote's paragraph was read for the proposal engine
         *   CONSEQUENCE
         *     it is placed from the read; one the read cannot settle alone goes on to its edges
         */
        if (quoteParagraphRead !== null && wholeQuoteHits !== null && fallbackWords !== null) {
            const paragraphReadPlacement = placementFromParagraphRead(quoteParagraphRead, wholeQuoteHits);
            if (paragraphReadPlacement.kind === "settled") {
                target.resolvedTarget = paragraphReadPlacement.target;
                /** WHEN the read placed the quote on Word's ranges CONSEQUENCE its edges are asked about struck text in this same sync. */
                if (paragraphReadPlacement.quoteEdges !== null && queueQuoteEdgesAgainstStruckStretches(target, paragraphReadPlacement.quoteEdges)) queuedAnything = true;
                continue;
            }
            /**
             * WHEN the paragraph no longer reads as the quote
             *   CONSEQUENCE
             *     the first word, relocated, which pass 4 moves off struck text like any other
             */
            if (paragraphReadPlacement.kind === "quoteGone") {
                target.relocationReasonOnFallback = "quote_not_in_paragraph";
                settleOnFirstWord(target, fallbackWords);
                continue;
            }
            target.widenedQuoteRange = paragraphReadPlacement.widenedQuoteRange;
        }
        if (target.openingEdgeHits === null || target.closingEdgeHits === null) continue;
        if (target.openingEdgeHits.items.length === 0 || target.closingEdgeHits.items.length === 0) continue;
        const openingHit = target.openingEdgeHits.items[0];
        target.closingEdgeRelations = target.closingEdgeHits.items.map((closingHit) => closingHit.compareLocationWith(openingHit));
        queuedAnything = true;
    }
    return queuedAnything;
};

/**
 * Settles every comment the passes left open: the edge span when the edges frame it,
 * the first word of the fallback range otherwise.
 *
 * CASES, per closing hit compared with the opening hit
 *   - after or adjacentAfter -> it closes the span; the target runs from the opening hit to it
 *   - anything else          -> it belongs to an earlier occurrence of the words; skipped
 *
 * CASES, when no closing hit frames the span
 *   - the paragraph read placed the quote on a range reaching past it -> that range, as placed:
 *                                                                       it holds the quoted words
 *   - otherwise                                                        -> the fallback below
 *
 * FALLBACK
 *   No edge found, no closing hit after the opening, a quote too short for edges, or a
 *   quote the paragraph read could not place: the first word of the fallback range, or
 *   the range itself when it has no word, relocated.
 */
const settleRemaining = (pending: PendingCommentTarget[]): void => {
    for (const target of pending) {
        if (target.resolvedTarget !== null) continue;
        const closingIndex = target.closingEdgeRelations.findIndex(
            (relation) => relation.value === Word.LocationRelation.after || relation.value === Word.LocationRelation.adjacentAfter,
        );
        if (closingIndex !== -1 && target.openingEdgeHits !== null && target.closingEdgeHits !== null) {
            target.resolvedTarget = asPlaced(target.openingEdgeHits.items[0].expandTo(target.closingEdgeHits.items[closingIndex]));
            continue;
        }
        /** WHEN the paragraph read placed the quote on a range reaching past it CONSEQUENCE that range, as placed: it holds the quoted words. */
        if (target.widenedQuoteRange !== null) {
            target.resolvedTarget = asPlaced(target.widenedQuoteRange);
            continue;
        }
        /** FALLBACK: no words were asked for; the fallback range itself. */
        if (target.fallbackWords === null) {
            target.resolvedTarget = fallbackTargetOf(target, target.fallbackRange);
            continue;
        }
        settleOnFirstWord(target, target.fallbackWords);
    }
};

/**
 * Pass 4, one round: for every target still looking for its shown first word, ask where
 * each struck stretch of the range sits against the next window of its words.
 *
 * WHY
 *   Word cuts words at spaces over the text it shows, struck words included, and the
 *   engine writes a replaced word as its insertion and its deletion side by side. The
 *   first word of a cell "$15,000" changed to "$12,000" reads "$12,000$15,000", and a
 *   whole-cell comment covered the struck "$15,000" as well (#2279 QA F2, section 4).
 *   A struck opening clause ("Notwithstanding the foregoing, ") is a run of words struck
 *   whole, so the shown first word can be any number of words in.
 *
 * CASES, per target
 *   - it settled on no first word, or pass 4 settled it -> nothing asked
 *   - Word cannot list tracked changes (WordApi 1.6)     -> settled: the first word is kept as Word cut it
 *   - the range holds no deleted change                 -> settled: the first word is all shown text
 *   - otherwise                                         -> every deleted change against the words after
 *                                                          those already found struck whole, as many as
 *                                                          were found struck and never fewer than
 *                                                          `WORDS_IN_THE_FIRST_STRUCK_TEXT_WINDOW`
 *
 * EXAMPLE
 *   round 1: words found struck 0 -> window words 0..12; all 12 struck
 *   round 2: words found struck 12 -> window words 12..24
 */
const queueStruckStretchesAgainstNextWords = (pending: PendingCommentTarget[]): boolean => {
    let queuedAnything = false;
    for (const target of pending) {
        target.struckStretchesAgainstWords = null;
        /** WHEN the target settled on no first word, or pass 4 already settled it CONSEQUENCE nothing more to ask. */
        if (target.wordsOfFirstWordTarget.length === 0 || target.firstWordSettled) continue;
        const struckChanges = struckChangesOf(target);
        /** WHEN Word could not list the range's tracked changes, or it holds no deleted change CONSEQUENCE the first word stands as Word cut it. */
        if (struckChanges.length === 0) {
            target.firstWordSettled = true;
            continue;
        }
        const firstWordIndex = target.wordsFoundStruckWhole;
        /**
         * The window: from the first word not yet found struck, as many words as were found struck so far.
         * EXAMPLE found 0 -> 0 + max(12, 0) = words 0..12; found 12 -> 12 + max(12, 12) = words 12..24
         */
        const windowWords = target.wordsOfFirstWordTarget.slice(firstWordIndex, firstWordIndex + Math.max(WORDS_IN_THE_FIRST_STRUCK_TEXT_WINDOW, firstWordIndex));
        target.struckStretchesAgainstWords = {
            struckChanges,
            firstWordIndex,
            relationOf: windowWords.map((word) => struckChanges.map((struckChange) => struckChange.getRange(Word.RangeLocation.whole).compareLocationWith(word))),
        };
        queuedAnything = true;
    }
    return queuedAnything;
};

/** Whether a struck stretch at `relation` to a word covers it whole: the word has no shown character. */
const struckStretchCoversTheWord = (relation: Word.LocationRelation): boolean =>
    [Word.LocationRelation.equal, Word.LocationRelation.contains, Word.LocationRelation.containsStart, Word.LocationRelation.containsEnd].includes(relation);

/** Whether a struck stretch at `relation` to a word opens it and ends inside it: the word's shown part follows the stretch. */
const struckStretchOpensTheWord = (relation: Word.LocationRelation): boolean =>
    [Word.LocationRelation.insideStart, Word.LocationRelation.overlapsBefore].includes(relation);

/** Whether a struck stretch at `relation` to a word starts inside it and runs to its end or past it: the word's shown part comes before the stretch. */
const struckStretchClosesTheWord = (relation: Word.LocationRelation): boolean =>
    [Word.LocationRelation.insideEnd, Word.LocationRelation.overlapsAfter].includes(relation);

/**
 * The shown part of one word Word cut, against the range's struck stretches: the word without
 * the struck stretch that opens it and the one that closes it; null when every character of it
 * is struck.
 *
 * CASES
 *   - a stretch covers the word whole                -> null: no shown word here
 *   - a stretch opens the word and ends inside it    -> the word from the stretch's end
 *   - a stretch starts inside the word and closes it -> the word up to the stretch's start
 *   - a stretch strictly inside the word             -> inside the shown part, as struck words
 *                                                       between a quote's own words are
 *   - no stretch reaches it                          -> the word as Word cut it
 *
 * WHY a stretch strictly inside the word stays
 *   Shown text of the word follows it: the engine writes a replaced word mid-sentence in
 *   Japanese, Chinese and Thai, which Word cuts as one range, and a user edits mid-word.
 *
 * EXAMPLE
 *   cell "+[$12,000]-[$15,000]", word "$12,000$15,000", "$15,000" closes it          -> "$12,000"
 *   "-[Fifteen ]+[Twelve] thousand", word "Fifteen Twelve"… cut at the space:
 *     "Fifteen" covered whole -> null; "Twelve" reached by none              -> "Twelve"
 *   "licen-[c]+[s]e", word "licencse", "c" strictly inside it                       -> "licencse"
 *   "本製品の+[品質]-[保証]期間は一年とする。", one word, "保証" strictly inside it -> the whole sentence
 *   "-[旧]新規約に従う-[ものとする]", one word, opened and closed                    -> "新規約に従う"
 */
const shownPartOfWord = (word: Word.Range, struckChanges: Word.TrackedChange[], relationsOfWord: OfficeExtension.ClientResult<Word.LocationRelation>[]): Word.Range | null => {
    /** NULL: a stretch covers the word whole. */
    if (relationsOfWord.some((relation) => struckStretchCoversTheWord(relation.value))) return null;
    const openingStretchIndex = relationsOfWord.findIndex((relation) => struckStretchOpensTheWord(relation.value));
    const closingStretchIndex = relationsOfWord.findIndex((relation) => struckStretchClosesTheWord(relation.value));
    /** WHEN no stretch opens or closes the word (both findIndex -1) CONSEQUENCE the word stands as Word cut it. */
    if (openingStretchIndex === -1 && closingStretchIndex === -1) return word;
    /** CASES: a stretch opens the word -> its shown part starts at that stretch's end; none does -> at the word's start. */
    const shownStart = openingStretchIndex === -1 ? word.getRange(Word.RangeLocation.start) : struckChanges[openingStretchIndex].getRange(Word.RangeLocation.end);
    /** CASES: a stretch closes the word -> its shown part ends at that stretch's start; none does -> at the word's end. */
    const shownEnd = closingStretchIndex === -1 ? word.getRange(Word.RangeLocation.end) : struckChanges[closingStretchIndex].getRange(Word.RangeLocation.start);
    return shownStart.expandTo(shownEnd);
};

/**
 * One round of pass 4 answered: moves each first-word target onto the first shown word of the
 * window asked about, trimmed of the struck stretch Word glued to it (`shownPartOfWord`).
 *
 * CASES, per target asked about this round
 *   - a word of the window has a shown part -> the target sits on it; settled
 *   - every word of the window is struck whole, and the range has words after it
 *                                           -> the next round asks about the words after it
 *   - every word of the range is struck whole
 *                                           -> EDGE CASE settled on the first word, as there is no shown
 *                                              word to sit on
 *
 * EXAMPLE
 *   "-[Notwithstanding the foregoing, ]the Supplier shall…"
 *   -> "Notwithstanding", "the", "foregoing," covered whole; the comment sits on "the"
 */
const moveFirstWordsOffStruckText = (pending: PendingCommentTarget[]): void => {
    for (const target of pending) {
        const struckStretches = target.struckStretchesAgainstWords;
        /** WHEN nothing was asked about this target this round CONSEQUENCE it stands as it is. */
        if (struckStretches === null) continue;
        for (const [wordOffset, relationsOfWord] of struckStretches.relationOf.entries()) {
            /** The word asked about at this offset of the window: EXAMPLE window from 12, offset 2 -> word 14. */
            const word = target.wordsOfFirstWordTarget[struckStretches.firstWordIndex + wordOffset];
            const shownPart = shownPartOfWord(word, struckStretches.struckChanges, relationsOfWord);
            /** WHEN the word is struck whole CONSEQUENCE it is counted, and the next word is tried. */
            if (shownPart === null) {
                target.wordsFoundStruckWhole += 1;
                continue;
            }
            /** The shown word, landing as the first word did: as placed, or relocated for the same reason. */
            target.resolvedTarget = fallbackTargetOf(target, shownPart);
            target.firstWordSettled = true;
            break;
        }
        /** EDGE CASE: every word of the range is struck whole; the first word stands. */
        if (target.wordsFoundStruckWhole >= target.wordsOfFirstWordTarget.length) target.firstWordSettled = true;
    }
};

/**
 * Pass 3 answered for the quotes placed from the paragraph read: each quote runs from the start
 * of its first range's shown part to the end of its last range's shown part (`shownPartOfWord`),
 * so a struck stretch Word glued before the quote or after it is left out.
 *
 * CASES, per end range
 *   - a struck stretch opens the first range   -> the quote starts where that stretch ends
 *   - a struck stretch closes the last range   -> the quote ends where that stretch starts
 *   - a struck stretch strictly inside a range,
 *     or one closing the first range or opening
 *     the last                                 -> inside the quote, as the struck words between
 *                                                its own words are
 *   - no stretch reaches the range             -> the range as Word cut it
 *
 * WHY the start of the one and the end of the other
 *   A stretch that closes the first range sits between the quote's own words when the quote runs
 *   on past that range, and one that opens the last range does too; only the quote's two outer
 *   ends are trimmed. A quote on one range takes both ends of that range's shown part.
 *
 * EXAMPLE
 *   quote "The monthly fee is $12,000", last range "$12,000$15,000", "$15,000" struck at its end
 *   -> the start of "The" expanded to the end of "$12,000": the struck "$15,000" left out
 *
 * FALLBACK
 *   A range every character of which is struck (`shownPartOfWord` null) cannot be a placed
 *   range, whose accepted text holds the quote's words; it stands as Word cut it.
 */
const trimQuoteEdgesOffStruckText = (pending: PendingCommentTarget[]): void => {
    for (const target of pending) {
        const edgesAgainstStruck = target.quoteEdgesAgainstStruckStretches;
        /** WHEN the target is no quote placed from the read in a range with struck text CONSEQUENCE it stands as it is. */
        if (edgesAgainstStruck === null) continue;
        const { firstRange, lastRange } = edgesAgainstStruck.quoteEdges;
        const shownFirst = shownPartOfWord(firstRange, edgesAgainstStruck.struckChanges, edgesAgainstStruck.relationsToFirstRange) ?? firstRange;
        const shownLast = shownPartOfWord(lastRange, edgesAgainstStruck.struckChanges, edgesAgainstStruck.relationsToLastRange) ?? lastRange;
        target.resolvedTarget = asPlaced(shownFirst.getRange(Word.RangeLocation.start).expandTo(shownLast.getRange(Word.RangeLocation.end)));
    }
};

/**
 * One of `commentTargets`'s first three passes, run as a round trip: the pass queues its
 * requests on the context and one `context.sync()` answers them. With a bulk apply timeline
 * active, the pass's queueing and its sync are recorded as one span (`recordBulkApplySpan`).
 *
 * WHY the span starts before the pass queues
 *   A pass walks every pending comment to build its searches and word reads, so a slow pass is
 *   slow in that queueing, in Word's answer, or in both. The span holds both, and a timeline
 *   shows which pass of a slow comment apply took the time.
 *
 * CASES
 *   - the pass queued a request  -> one sync, then the span `passSpanName` with `{ entries }`
 *   - it queued none             -> no sync and no span: the pass did not run
 *   - the sync throws            -> the span is recorded with the time spent, and the error goes on
 *
 * EXAMPLE
 *   11 comments, pass 1 queues 8 searches -> 1 sync, the span comment_target_pass_1 { entries: 11 }
 *   11 comments, pass 3 queues nothing    -> no sync, no comment_target_pass_3 span
 */
const syncWhenPassQueued = async (
    wordRequestContext: Word.RequestContext,
    passSpanName: string,
    queuePass: () => boolean,
    pendingEntryCount: number,
): Promise<void> => {
    const passStartedAtMs = bulkApplyClockMs();
    /**
     * WHEN the pass queued no request
     *   CONSEQUENCE
     *     Return: the pass costs no round trip and records no span.
     */
    if (!queuePass()) return;
    try {
        await wordRequestContext.sync();
    } finally {
        recordBulkApplySpan(passSpanName, passStartedAtMs, { entries: pendingEntryCount });
    }
};

/**
 * Where a batch of review comments are written, from the ranges their bookmarks
 * resolve to and the placement the review decided for each (`CommentPlacement`), in
 * the order the requests were given, each saying whether it landed where it was
 * placed.
 *
 * Every request must share one `Word.RequestContext`: the batch syncs the first
 * request's context, and Word answers only the requests queued on it.
 *
 * CASES, per comment
 *   - placement null     -> the bookmarked range itself: a production review's comment
 *                           covers the whole range as it always has
 *   - "first_word"       -> the range's first word
 *   - "span"             -> the first occurrence of the quoted words in the range: on the
 *                           proposal engine found in the paragraph's accepted text and
 *                           placed on Word's ranges, struck words between them included;
 *                           without it Word's search for the whole quote, then for the
 *                           stretch between its opening and closing words
 *                           FALLBACK the range's first word, relocated, when the words are
 *                           not placed (the paragraph changed since the review, or the
 *                           quote is empty)
 *   - "after_paragraph"  -> the first word of the paragraph following the range's first
 *                           paragraph, which is the paragraph the remedy inserted
 *                           FALLBACK the range's first word, relocated, when no paragraph follows
 *
 * Every first word is the first shown word: words struck whole are passed over, and a
 * struck stretch Word glues to the word is trimmed off (`moveFirstWordsOffStruckText`). A
 * quote placed from the paragraph read leaves out a struck stretch glued before its first
 * word or after its last the same way (`trimQuoteEdgesOffStruckText`).
 *
 * Round trips for the whole batch, however many comments it holds:
 *   - pass 1: one sync, when any comment is placed; on the proposal engine it also answers the
 *             batch's one read of the document body, when a request names its bookmarks and the
 *             merges' read (`bodyReadOfTheMerges`) does not already cover them
 *   - paragraph reads: one sync, when any quoted span read from the body was not settled on its one
 *             whole-quote hit (`settleUnchangedParagraphTargets`): its words, and its OOXML
 *             when the body read cut no page for it
 *   - pass 2: one sync, when any span was read or missed, or any comment is after_paragraph
 *   - pass 3: one sync, when any missed span found both of its edges, or a quote placed from
 *             the paragraph read sits in a range with struck text (`trimQuoteEdgesOffStruckText`)
 *   - pass 4: one sync, when any first word sits in a range that holds a struck stretch, and
 *             one more for each window of words struck whole before the shown one
 *             (12, then 12, 24, 48 words …)
 *
 * WHY each pass is a span of the bulk apply timeline
 *   A slow comment apply is slow in one of its passes. With a timeline active
 *   (`beginBulkApplyTimeline`) the batch records `comment_target_reads_quotes`,
 *   `comment_target_pass_1` to `comment_target_pass_3` and `comment_target_paragraph_reads` (each
 *   only when the pass ran), `comment_target_fast_path` (when a quoted span is read from the body)
 *   and `comment_target_struck_rounds`, and a single-comment apply with none active records nothing.
 *
 * EXAMPLE
 *   11 comments without the proposal engine: 8 spans that hit, 2 spans that miss, 1 after_paragraph
 *   -> 3 syncs, where resolving them one at a time cost 8 + 2 * 4 + 2 = 18
 *   11 spans on the proposal engine -> 2 syncs
 *   20 spans on the proposal engine, bookmarks on every request, every paragraph unchanged and
 *   every quote found once -> 1 sync and 1 body read, no read of a paragraph
 *   the same 20 after a run's merges, whose read covers all 20 bookmarks -> 1 sync and no body read
 */
export const commentTargets = async (requests: CommentTargetRequest[], bodyReadOfTheMerges: BulkApplyBodyRead | null = null): Promise<CommentTarget[]> => {
    if (requests.length === 0) return [];
    const context = requests[0].bookmarkedRange.context;
    /**
     * CASES, by the account's proposal-engine flag, read once for the batch
     *   - on                       -> a quoted span is found in its paragraph's accepted text, its
     *                                 edges cut in the redline diff's units
     *   - off, or the lookup fails -> it is found by Word's search, its edges split at spaces, as
     *                                 they always were
     *
     * WHY the lookup is a span
     *   The first batch of a run waits for the account's `/me` read, and every later batch takes the
     *   cached answer, so the span is long once and near zero after it.
     */
    const readsQuotesFromTheDocument = (await timedBulkApplySpan("comment_target_reads_quotes", () => ProposalEngineAccess.current())).enabled;
    const quoteEdgeSearchTextsOf = readsQuotesFromTheDocument ? quoteEdgeSearchTextsInUnits : quoteEdgeSearchTextsSplitAtSpaces;
    const pending = requests.map(pendingCommentTargetFor);
    const batchBodyPages = new BatchBodyPages(bodyReadOfTheMerges);
    await syncWhenPassQueued(context, "comment_target_pass_1", () => queueFirstPass(pending, readsQuotesFromTheDocument, batchBodyPages), pending.length);
    settleUnchangedParagraphTargets(pending, batchBodyPages);
    await syncWhenPassQueued(context, "comment_target_paragraph_reads", () => queueParagraphReadsOfUnsettledTargets(pending), pending.length);
    await syncWhenPassQueued(context, "comment_target_pass_2", () => queueSecondPass(pending, quoteEdgeSearchTextsOf), pending.length);
    await syncWhenPassQueued(context, "comment_target_pass_3", () => queueThirdPass(pending), pending.length);
    settleRemaining(pending);
    trimQuoteEdgesOffStruckText(pending);
    const struckRoundsStartedAtMs = bulkApplyClockMs();
    let struckSyncRoundCount = 0;
    try {
        /** One round per window of words, until every first word is settled (`queueStruckStretchesAgainstNextWords`). */
        while (queueStruckStretchesAgainstNextWords(pending)) {
            await context.sync();
            /**
             * EXAMPLE
             *   a first word behind a struck opening clause of 14 words: a window of 12 words, then one
             *   of the next 12 -> 2 syncs, so the count goes 0 + 1 = 1, then 1 + 1 = 2
             */
            struckSyncRoundCount += 1;
            moveFirstWordsOffStruckText(pending);
        }
    } finally {
        /**
         * CASES
         *   - no first word sits in a range with a struck stretch -> the span with { rounds: 0 }, no sync
         *   - the loop ran, or a sync threw                       -> the span with the rounds whose sync returned
         */
        recordBulkApplySpan("comment_target_struck_rounds", struckRoundsStartedAtMs, { rounds: struckSyncRoundCount });
    }
    return pending.map((target) => target.resolvedTarget as CommentTarget);
};

/**
 * Where one review comment is written: `commentTargets` for a batch of one.
 *
 * EXAMPLE
 *   range "Supplier warrants the Products for one year.", "span", "for one year"
 *   -> the range over "for one year", as placed
 */
export const commentTarget = async (request: CommentTargetRequest): Promise<CommentTarget> => {
    const [target] = await commentTargets([request]);
    return target;
};
